import { z } from 'zod';

import { accessIdentifierSchema, accessPasswordSchema, sitePrefixFor } from './site-prefix';

/**
 * Das Administrator-Konto **innerhalb** einer Anwendung — nicht auf dem Server.
 *
 * ## Wozu es überhaupt gehört
 *
 * Seit M5 gibt es Website-Typen, die sich beim ersten Anwenden selbst
 * einrichten. Eine solche Einrichtung braucht ein erstes Konto, sonst steht der
 * Installationsassistent offen im Netz und der erste Besucher wird Administrator
 * — samt der Möglichkeit, PHP auf dem Container auszuführen. Das ist kein
 * Komfortthema, sondern eine Übernahme.
 *
 * Anders als Datenbank und SFTP-Zugang bekommt **nicht** jede Site eines: Eine
 * statische Seite hat keinen Administrator. Das Konto entsteht genau dann, wenn
 * der Blueprint eine Einrichtungshandlung trägt, die es braucht.
 *
 * ## Warum Benutzername und Passwort aus demselben Vorrat kommen wie überall
 *
 * `accessIdentifierSchema` und `accessPasswordSchema` sind für Datenbanken
 * entstanden, wo `_` und `%` in einem `GRANT` Platzhalter wären. Hier tragen
 * dieselben Schemas eine zweite Last: Beide Werte werden Teil der
 * Argumentliste, die der **Agent** für die Einrichtung zusammensetzt. Ein Wert
 * aus `[A-Za-z0-9._~-]` kann dort nichts anderes bedeuten, als er aussieht — er
 * enthält kein Leerzeichen, das ein Argument teilte, und keinen Bindestrich am
 * Anfang, der ihn zu einem Schalter machte.
 *
 * Das ist die zweite von zwei Bremsen. Die erste ist, dass der Agent die
 * Argumentliste überhaupt selbst bildet und der Master kein einziges Element
 * davon liefert (siehe `provisioning/post-install.ts`). Eine davon allein wäre
 * zu wenig.
 */

// -- Namensbildung -----------------------------------------------------------

/**
 * Benutzername des Administrators einer Site, z. B. `ks04e87b83c1d2a1`.
 *
 * Das `a` unterscheidet ihn von Datenbank (`d`), Datenbankbenutzer (`u`) und
 * SFTP-Konto (`s`); alle vier teilen einen Präfix und damit einen Namensraum.
 *
 * Abgeleitet und nicht `admin`: Ein vorhersagbarer Name ist die halbe Arbeit
 * eines Anmeldeversuchs von außen, und die Anmeldemaske einer Website steht
 * naturgemäß offen im Internet.
 */
export function buildAdminUsername(siteId: string): string {
  return accessIdentifierSchema.parse(`${sitePrefixFor(siteId)}a1`);
}

// -- E-Mail ------------------------------------------------------------------

/**
 * Die E-Mail-Adresse des Administrator-Kontos.
 *
 * Bewusst eng und ausdrücklich **nicht** RFC 5322: Der Wert wird vom Master aus
 * dem Hostnamen der Site erzeugt (`admin@<hostname>`) und niemals eingegeben.
 * Ein vollständiger Adressparser wäre hier Aufwand für einen Fall, den es nicht
 * gibt — und eine zusätzliche Gelegenheit, etwas durchzulassen.
 *
 * Ein Kundenmodell mit einer echten Adresse gibt es bis M6 nicht. Bis dahin ist
 * diese Adresse eine Pflichtangabe der Anwendung und kein Postfach; der
 * Betreiber ändert sie im Backend, sobald er den Kunden kennt.
 */
export const adminEmailSchema = z
  .string()
  .max(254, { message: 'Die E-Mail-Adresse ist zu lang.' })
  .regex(/^[a-z\d][a-z\d._-]*@[a-z\d]([a-z\d-]*[a-z\d])?(?:\.[a-z\d]([a-z\d-]*[a-z\d])?)+$/, {
    message:
      'Muss eine einfache E-Mail-Adresse in Kleinbuchstaben sein, z. B. "admin@kunde.de". Sie wird aus dem Hostnamen der Site erzeugt und nicht eingegeben.',
  });

// -- Das Konto in der Spezifikation ------------------------------------------

/**
 * Die Zugangsdaten, mit denen die Anwendung eingerichtet wird.
 *
 * Das Passwort steht hier im Klartext, aus demselben Grund wie bei Datenbanken
 * und SFTP-Konten: Die Spezifikation ist alles, was der Agent braucht — er
 * stellt keine Rückfragen und holt sich nichts nach. Getragen wird das an
 * denselben drei Stellen: `site.spec` liegt beim Master verschlüsselt, die Queue
 * transportiert nur eine Operations-ID, und die Log-Redaction schwärzt `spec`
 * als Ganzes.
 */
export const siteAdminSpecSchema = z.object({
  username: accessIdentifierSchema,
  email: adminEmailSchema,
  password: accessPasswordSchema,
});

export type SiteAdminSpec = z.infer<typeof siteAdminSpecSchema>;

// -- Außensicht --------------------------------------------------------------

/**
 * Was der Betreiber über das Administrator-Konto zu sehen bekommt.
 *
 * **Ohne Passwort** — dieselbe Haltung wie beim Bootstrap-Token, bei den
 * Datenbanken und bei SFTP: Es steht genau einmal in der Antwort auf die
 * Ausgabe der Zugangsdaten und danach nie wieder.
 */
export const siteAdminViewSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  username: accessIdentifierSchema,
  email: adminEmailSchema,
  /**
   * Fährt der Worker bereits eine Spezifikation, die dieses Konto nennt?
   *
   * `false` unmittelbar nach dem Bestellen ist der Normalfall und kein Fehler.
   * Erst wenn hier `true` steht, hat die Einrichtung auf dem Node tatsächlich
   * stattgefunden — vorher ist das Passwort ein Versprechen und keine Anmeldung.
   */
  liveOnNode: z.boolean(),
  createdAt: z.string(),
});

export type SiteAdminView = z.infer<typeof siteAdminViewSchema>;

/**
 * Die vollständigen Zugangsdaten — der einzige Weg, auf dem ein
 * Administrator-Passwort den Master verlässt.
 *
 * `loginUrl` steht dabei, weil die Antwort sonst unvollständig wäre: Der
 * Betreiber müsste sich aus Hostnamen und Website-Typ selbst zusammenreimen,
 * wohin er sich anmeldet. Sie zeigt auf den **kanonischen** Namen der Site — den
 * Apex, sobald es eine Kundendomain gibt.
 */
export const siteAdminCredentialsSchema = siteAdminViewSchema.extend({
  password: accessPasswordSchema,
  loginUrl: z.string(),
});

export type SiteAdminCredentials = z.infer<typeof siteAdminCredentialsSchema>;
